import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BellOutlined,
  WarningOutlined,
  FlagOutlined,
  PlayCircleOutlined,
  EnvironmentOutlined,
  DownOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { Pill } from '../ui';
import {
  projectAlerts,
  scheduleLabel,
  formatScheduleDate,
  SCHEDULE_TONE,
  SOON_DAYS,
  ScheduleState,
} from './projectSchedule';
import './ProjectAlerts.css';

interface AlertProject {
  id: number;
  name: string;
  location?: string | null;
  status: string;
  start_date?: string | null;
  end_date?: string | null;
}

const ICONS: Partial<Record<ScheduleState, React.ReactNode>> = {
  overdue: <WarningOutlined />,
  endsSoon: <FlagOutlined />,
  startsSoon: <PlayCircleOutlined />,
};

const PREVIEW_COUNT = 6;

/**
 * Active projects that start or end within SOON_DAYS, or are past their end date. Renders
 * nothing when there are none, so it can sit at the top of any page.
 */
const ProjectAlerts: React.FC<{
  projects: AlertProject[];
  onSelect: (projectId: number) => void;
  /** Shown in the header, e.g. a link to the Projects page */
  action?: React.ReactNode;
  className?: string;
}> = ({ projects, onSelect, action, className }) => {
  const { t, i18n } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const alerts = useMemo(() => projectAlerts(projects), [projects]);

  if (alerts.length === 0) return null;

  const overdue = alerts.filter((entry) => entry.schedule.state === 'overdue').length;
  const visible = showAll ? alerts : alerts.slice(0, PREVIEW_COUNT);

  return (
    <section
      className={`e22-card pa-card ${overdue > 0 ? 'pa-card--danger' : ''} ${className ?? ''}`}
      aria-label={t('projects.alerts.title')}
    >
      <div className="pa-head">
        <span className="pa-head-icon" aria-hidden>
          <BellOutlined />
        </span>
        <div className="e22-section-heading">
          <h2 className="e22-section-title">{t('projects.alerts.title')}</h2>
          <div className="e22-section-subtitle">
            {overdue > 0
              ? t('projects.alerts.subtitleOverdue', { count: alerts.length, overdue })
              : t('projects.alerts.subtitle', { count: alerts.length, days: SOON_DAYS })}
          </div>
        </div>
        {action ? <div className="e22-section-action">{action}</div> : null}
      </div>

      <div className="pa-grid">
        {visible.map(({ project, schedule }) => {
          const date =
            schedule.state === 'startsSoon'
              ? t('projects.alerts.startDate', { date: formatScheduleDate(schedule.start!, i18n.language) })
              : t(schedule.state === 'overdue' ? 'projects.alerts.endedDate' : 'projects.alerts.endDate', {
                  date: formatScheduleDate(schedule.end!, i18n.language),
                });
          return (
            <button
              key={project.id}
              type="button"
              className={`pa-item pa-item--${schedule.state}`}
              onClick={() => onSelect(project.id)}
            >
              <span className="pa-item-icon" aria-hidden>
                {ICONS[schedule.state]}
              </span>
              <span className="pa-item-body">
                <span className="pa-item-name">{project.name}</span>
                <span className="pa-item-meta">
                  {project.location ? (
                    <>
                      <EnvironmentOutlined aria-hidden /> {project.location} ·{' '}
                    </>
                  ) : null}
                  {date}
                </span>
              </span>
              <Pill tone={SCHEDULE_TONE[schedule.state]}>{scheduleLabel(t, schedule)}</Pill>
            </button>
          );
        })}
      </div>

      {alerts.length > PREVIEW_COUNT && (
        <div className="e22-show-more">
          <button type="button" className="e22-link-button" onClick={() => setShowAll(!showAll)}>
            {showAll ? t('dashboard.showLess') : t('dashboard.showAll', { count: alerts.length })}
            {showAll ? <UpOutlined /> : <DownOutlined />}
          </button>
        </div>
      )}
    </section>
  );
};

export default ProjectAlerts;
